// Account manager — Supabase identity (anonymous guest, email/password,
// Google) + auto-sync of game state to the backend.
//
// Identity is owned by Supabase Auth: the SDK persists the session and a
// short-lived, auto-refreshing access token. Every boot: ensure a session
// (anonymous if none), read the account meta from /api/account/me, pull server
// state if newer than localStorage, then mirror local→server on a 5s timer.
//
// The backend/Supabase are optional. If unreachable, we fall back to
// localStorage-only mode and the rest of the game keeps working.

import { Events, emit, on } from '../ui/eventBus.js'
import { state, persist, setPersistHook, freezePersistence } from './state.js'
import { migrateState, clear as clearLocalSave } from './save.js'
import { awardServerFirst } from './achievementManager.js'
import { supabase } from './supabase.js'

const SYNC_INTERVAL_MS = 5_000

let userId = null
let online = false
let dirty = false
let changeSeq = 0       // counts local changes; lets a save know if anything changed while it was in flight
let staleLock = false   // set when the server says our copy is out of date: stop pushing, load the latest instead

// ── Whose game is on this device, and which server revision it is based on ───────────────────────────────────────
// The saved game in this browser belongs to ONE account (OWNER). REV is the server revision it was last in sync with: a save
// is sent as "built on revision N" and the server refuses it if the game has moved on (another tab or device saved since),
// so a stale copy can never overwrite newer progress or purchased coins. DIRTY remembers unsaved changes across reloads.
const OWNER_KEY = 'mao_sync_owner'
const REV_KEY = 'mao_sync_rev'
const DIRTY_KEY = 'mao_sync_dirty'
const BACKUP_KEY = 'mao_sync_conflict_backup'   // the local copy we set aside when the server's newer one won (for support)
const NOTICE_KEY = 'mao_sync_notice'            // one-line message shown after the page reloads
const lsGet = (k) => { try { return localStorage.getItem(k) } catch { return null } }
const lsSet = (k, v) => { try { localStorage.setItem(k, v) } catch { /* private mode */ } }
const lsDel = (k) => { try { localStorage.removeItem(k) } catch { /* ignore */ } }
let serverRev = lsGet(REV_KEY) == null ? null : Number(lsGet(REV_KEY))
function setServerRev(r) { serverRev = Number.isFinite(Number(r)) ? Number(r) : null; if (serverRev == null) lsDel(REV_KEY); else lsSet(REV_KEY, String(serverRev)) }
function setDirty(v) { dirty = v; if (v) lsSet(DIRTY_KEY, '1'); else lsDel(DIRTY_KEY) }
if (lsGet(DIRTY_KEY)) dirty = true
// Every change to the game (anything that saves) marks it unsaved.
setPersistHook(() => { changeSeq += 1; if (!dirty) setDirty(true) })

// Forget the saved game on this device completely (used on sign-out and when the account changes).
function wipeLocalGame() {
  clearLocalSave()
  for (const k of [OWNER_KEY, REV_KEY, DIRTY_KEY]) lsDel(k)
  serverRev = null
  dirty = false
}
// Make sure the game on this device belongs to `id`. If it belonged to someone else, wipe it and reload so the new player
// starts from THEIR saved game — never inherits (or overwrites with) another player's. Returns 'reload' when it reloaded.
let gameUser = null // the account whose game is loaded in memory right now
let leaving = false // a sign-out we started ourselves (its auth event is expected)
function bindOwner(id) {
  const owner = lsGet(OWNER_KEY)
  if (owner && owner !== id) {
    // Freeze saving FIRST: this page is still running the other player's game until the reload lands, and any save in that
    // window would write it straight back to disk — and then it would belong to the new player.
    freezePersistence()
    wipeLocalGame()
    lsSet(OWNER_KEY, id)
    if (typeof window !== 'undefined') window.location.reload()
    return 'reload'
  }
  if (!owner) lsSet(OWNER_KEY, id)
  gameUser = id
  return 'ok'
}
// The login changed underneath a running game (another tab signed in as someone else, or signed out). The game in memory
// is not that player's, so it must neither save nor be shown: set any unsaved changes aside, forget the game on this
// device and reload to start from the right account's saved game.
function identityChanged() {
  if (!gameUser) return
  freezePersistence()
  staleLock = true
  if (dirty) { try { lsSet(BACKUP_KEY, JSON.stringify({ at: Date.now(), reason: 'account-changed', owner: gameUser, state })) } catch { /* too big */ } }
  gameUser = null
  wipeLocalGame()
  if (typeof window !== 'undefined') window.location.reload()
}
let email = null
let isAdmin = false
let membership = 'FREE'
let gender = null // the Mao's gender as the SERVER has locked it (null = not chosen yet)
let currentStreak = 0
let longestStreak = 0
let activeHoliday = null
let holidaysSeen = {}

export function getAccountInfo() {
  const loggedIn = !!userId
  return { userId, online, loggedIn, email, isAdmin, membership, gender, isRegistered: loggedIn, currentStreak, longestStreak, blockedReason, lastConflict }
}

export function getStreak() {
  return { current: currentStreak, longest: longestStreak }
}

export function getActiveHoliday() {
  return activeHoliday
}

export function getHolidaysSeen() {
  return holidaysSeen
}

// Apply streak fields from a /me response. Emits StreakChange when the value
// moves (any direction — bump, milestone, reset). Achievement triggers and
// the HUD chip both listen.
function applyServerStreak(me) {
  if (!me) return
  const nextC = me.currentStreak ?? 0
  const nextL = me.longestStreak ?? 0
  const changed = nextC !== currentStreak || nextL !== longestStreak
  currentStreak = nextC
  longestStreak = nextL
  if (changed) emit(Events.StreakChange, { current: currentStreak, longest: longestStreak })
  // Pending milestone bonus — claim immediately so the wealth lands as soon
  // as the player loads. Server is idempotent (clears its own copy on the
  // first claim) so a second /me hit won't double-pay.
  if (me.pendingStreakReward) {
    claimPendingStreakReward(me.pendingStreakReward)
  }
}

// Apply holiday fields from a /me response. Two signals:
//   • HolidayChange — fires when activeHoliday transitions (none → festival,
//     or festival ends mid-session) — the HUD banner subscribes
//   • HolidayParticipationStamp — fires when holidaysSeen GAINS a token (the
//     server stamps on auth during a window); the achievement manager
//     subscribes to award the family badge + the toast subscribes for the
//     "Welcome to the festival" celebration
function applyServerHoliday(me) {
  if (!me) return
  const nextActive = me.activeHoliday ?? null
  const prevToken = activeHoliday?.token ?? null
  const nextToken = nextActive?.token ?? null
  if (prevToken !== nextToken) {
    activeHoliday = nextActive
    emit(Events.HolidayChange, { holiday: activeHoliday })
  }
  const nextSeen = me.holidaysSeen ?? {}
  for (const token of Object.keys(nextSeen)) {
    if (!holidaysSeen[token]) {
      // New stamp — fire BEFORE we update the local mirror so the listener
      // can decide whether it's the player's first time in this family.
      emit(Events.HolidayParticipationStamp, { token, at: nextSeen[token], holiday: activeHoliday })
    }
  }
  holidaysSeen = nextSeen
}

async function claimPendingStreakReward(_preview) {
  try {
    const { reward } = await api('/api/account/streak/claim', { method: 'POST', body: '{}' })
    if (!reward) return // already claimed (lost the race against another tab)
    emit(Events.StreakRewardClaimed, reward)
  } catch (err) {
    console.warn('[streak] claim failed:', err.message)
    // Best-effort retry on next /me — server still holds the unclaimed reward.
  }
}

export function isMember() {
  return membership === 'KINGDOM_CLUB'
}

async function api(path, opts = {}) {
  const headers = { 'content-type': 'application/json', ...(opts.headers ?? {}) }
  // Attach the live Supabase access token (auto-refreshed by the SDK) unless the
  // caller explicitly set an authorization header (e.g. '' for public routes).
  if (!('authorization' in headers)) {
    const { data } = await supabase.auth.getSession()
    const tok = data.session?.access_token
    if (tok) headers.authorization = `Bearer ${tok}`
  }
  const res = await fetch(path, { ...opts, headers })
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`)
    err.status = res.status
    try { err.body = await res.json() } catch { err.body = null }
    throw err
  }
  return res.json()
}

// Read account meta from the server and apply membership / firsts / streak /
// holiday side effects. Shared by init, signup, login, claim, refreshMembership.
async function refreshAccountMeta() {
  const me = await api('/api/account/me')
  email = me.email ?? email
  isAdmin = !!me.isAdmin
  gender = me.gender ?? null
  const next = me.membership ?? 'FREE'
  const membershipChanged = next !== membership
  membership = next
  applyServerFirsts(me.firsts)
  applyServerStreak(me)
  applyServerHoliday(me)
  if (membershipChanged) emit(Events.MembershipChange, { tier: membership })
  return me
}

export async function init() {
  // Keep our local mirror of identity in sync with the SDK (sign-in, token
  // refresh, sign-out, OAuth redirect handoff all flow through here).
  supabase.auth.onAuthStateChange((_event, session) => {
    const next = session?.user?.id ?? null
    userId = next
    email = session?.user?.email ?? email
    if (gameUser && next !== gameUser && !leaving) identityChanged()
  })

  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (session) {
      // Returning, logged-in account: adopt its server state + meta.
      userId = session.user?.id ?? null
      email = session.user?.email ?? null
      if (bindOwner(userId) === 'reload') return // another player's game was on this device: reloading to start clean
      await ensureTabLock()
      if (blockedReason) { online = true; emit(Events.AccountReady, { userId, online: true, loggedIn: true, email, isAdmin, membership }); return }
      await refreshAccountMeta()
      await syncOnSession() // returning device — adopt the server's game if it has moved on
      online = true
      emit(Events.AccountReady, { userId, online: true, loggedIn: true, email, isAdmin, membership })
    } else {
      // No session → logged OUT. We don't create anonymous guests; the player
      // must Register (adopt-a-Mao) or Log in. `online` stays true so the
      // header surfaces the Log in / Register buttons (vs hard-offline).
      userId = null
      online = true
      emit(Events.AccountReady, { userId: null, online: true, loggedIn: false })
    }
  } catch (err) {
    online = false
    emit(Events.AccountReady, { userId: null, online: false, loggedIn: false, error: String(err) })
    console.warn('[account] Supabase/server unreachable:', err.message ?? err)
  }

  // Saving: every 5 seconds if anything changed (setPersistHook above marks EVERY change), and immediately when the tab is
  // hidden or closed, so the last action before an abrupt close isn't lost.
  setInterval(async () => {
    if (!online || !userId || !dirty || staleLock) return
    try {
      await pushState()
    } catch (err) {
      console.warn('[account] push failed:', err.message) // stays dirty → retried next tick
    }
  }, SYNC_INTERVAL_MS)

  const flush = () => {
    if (!online || !userId || !dirty || staleLock) return
    pushState({ keepalive: true }).catch(() => { /* stays dirty; retried */ })
  }
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush() })
  window.addEventListener('pagehide', flush)
}

// Replace the game in memory with the server's copy. Mutate in place so existing module references keep working.
function adoptRemote(remote) {
  for (const key of Object.keys(state)) delete state[key]
  Object.assign(state, remote.state)
  migrateState(state) // the server copy may predate the hex grid
  state.updatedAt = remote.updatedAt ?? Date.now()
  setServerRev(remote.rev ?? 0)
  persist() // mirror the server's game into localStorage too
  setDirty(false) // (persist() marks dirty; what we just adopted IS the server's copy)
  emit(Events.AccountStatePulled)
  emit(Events.KingdomChange) // rebuild scene/UI against the adopted state
}

// On sign-in / page load with a session: line this device up with the server.
//   • server has no game yet (new account)      → keep what we have; it is saved on the next tick
//   • server game is at the revision we hold    → in sync (anything unsaved is sent by the ticker)
//   • server game has moved on / we hold none   → the server's game wins; if we had unsaved local changes, set that copy
//                                                 aside (BACKUP_KEY) so nothing is ever silently destroyed
async function syncOnSession() {
  const remote = await api('/api/account/state')
  if (!remote?.state) return
  const rev = Number(remote.rev ?? 0)
  if (serverRev != null && serverRev === rev) return
  if (dirty && serverRev != null) {
    try { lsSet(BACKUP_KEY, JSON.stringify({ at: Date.now(), localRev: serverRev, serverRev: rev, state })) } catch { /* too big */ }
  }
  adoptRemote(remote)
}

// ── One active window at a time ───────────────────────────────────────────────────────────────────────────────────
// A game saved from two windows at once would keep overwriting itself. So only ONE window plays and saves; any other is
// blocked behind a "Play here instead" screen (src/ui/sessionBlocked.js) until the player chooses to switch.
//   • same browser: detected up-front with a Web Lock (the first window holds it)
//   • another device (or an old window): the server refuses our save as out of date (409)
// A blocked window stops saving completely, and sets its unsaved changes aside (BACKUP_KEY) so nothing is destroyed silently.
let blockedReason = null
function blockSession(reason) {
  if (blockedReason) return
  blockedReason = reason
  staleLock = true
  try { lsSet(BACKUP_KEY, JSON.stringify({ at: Date.now(), reason, localRev: serverRev, state })) } catch { /* too big */ }
  freezePersistence()
  emit(Events.SessionBlocked, { reason })
}

const LOCK_NAME = 'mao-kingdom-game'
// Try to become THE window for this browser. Resolves true if we hold it. With steal=true we take it from the other window,
// which is told (its request rejects), saves one last time, and blocks itself.
function acquireTabLock(steal = false) {
  if (typeof navigator === 'undefined' || !navigator.locks) return Promise.resolve(true) // very old browser: no protection
  return new Promise((resolve) => {
    navigator.locks.request(LOCK_NAME, steal ? { steal: true } : { ifAvailable: true }, (lock) => {
      if (!lock) { resolve(false); return Promise.resolve() }
      resolve(true)
      return new Promise(() => {}) // hold it for the life of this window
    }).catch(async () => { // somebody stole it from us
      if (userId && dirty && !staleLock) { try { await pushState() } catch { /* best effort */ } }
      blockSession('tab')
    })
  })
}
async function ensureTabLock() {
  if (!(await acquireTabLock())) blockSession('tab')
}

// The player chose "Play here instead": become the active window, take the latest saved game and reload.
export async function takeOver() {
  if (blockedReason === 'tab') { await acquireTabLock(true); await new Promise((r) => setTimeout(r, 1800)) } // let the other window finish its last save
  for (const k of [REV_KEY, DIRTY_KEY]) lsDel(k) // the reload starts from the server's game (no revision → it adopts the server's copy)
  clearLocalSave()
  try { sessionStorage.setItem(NOTICE_KEY, 'Playing here now. The latest saved game was loaded.') } catch { /* ignore */ }
  window.location.reload()
}
// One-line message shown after a takeover reload (read once by main.js).
export function takeSyncNotice() {
  try { const m = sessionStorage.getItem(NOTICE_KEY); sessionStorage.removeItem(NOTICE_KEY); return m } catch { return null }
}

// One save at a time per window. Two saves in flight would both carry the same revision: the second would be refused as
// "out of date" by the first one's own success, and we would wrongly conclude another window had saved. A page that is
// closing (keepalive) sends immediately instead of waiting its turn.
let pushTail = Promise.resolve()
let lastConflict = null // what the server last said when it refused a save (for support / tests)
function pushState(opts = {}) {
  if (opts.keepalive) return pushStateNow(opts)
  const run = () => pushStateNow(opts)
  const p = pushTail.then(run, run)
  pushTail = p.then(() => {}, () => {})
  return p
}
async function pushStateNow({ keepalive = false } = {}) {
  if (staleLock) return
  const seq = changeSeq
  state.updatedAt = Date.now()
  const baseRev = serverRev
  let result
  try {
    result = await api('/api/account/state', {
      method: 'POST',
      body: JSON.stringify({ state, baseRev, owner: gameUser }),
      keepalive, // lets the save complete during tab close/unload
    })
  } catch (err) {
    if (err.status === 409) lastConflict = { at: Date.now(), baseRev, body: err.body ?? null }
    if (err.status === 409 && err.body?.error === 'wrong_account') { identityChanged(); return } // signed in as someone else meanwhile
    if (err.status === 409) { blockSession('device'); return }   // the game moved on elsewhere: stop saving, ask the player
    throw err
  }
  setServerRev(result.rev)
  if (changeSeq === seq) setDirty(false) // nothing changed while the save was in flight
  emit(Events.AccountStatePushed, { updatedAt: result.updatedAt })
}

export async function manualPull() {
  if (!online) return { ok: false, reason: 'offline' }
  try {
    const remote = await api('/api/account/state')
    if (!remote?.state) return { ok: false, reason: 'no state on server' }
    adoptRemote(remote)
    return { ok: true }
  } catch (err) {
    return { ok: false, reason: err.message }
  }
}

export async function manualPush() {
  if (!online) return { ok: false, reason: 'offline' }
  try {
    await pushState()
    return { ok: true }
  } catch (err) {
    return { ok: false, reason: err.message }
  }
}

// ── Auth (Supabase) ─────────────────────────────────────────────────────────
// claimAccount: convert the CURRENT anonymous guest into a permanent
// email/password account. Supabase keeps the SAME user UUID, so the player's
// server-side game row (keyed by that UUID) is preserved automatically. Use
// this for the in-game "Create Account" CTA. (Requires "Confirm email" OFF in
// the Supabase project for the conversion to take effect immediately.)
export async function claimAccount(emailInput, password) {
  if (!online) return { ok: false, reason: 'offline' }
  try {
    const { data, error } = await supabase.auth.updateUser({ email: emailInput, password })
    if (error) throw error
    email = data.user?.email ?? emailInput
    bindOwner(userId)
    await pushState() // ensure the server has the latest progress under this UUID
    await refreshAccountMeta()
    emit(Events.AccountReady, { userId, online: true, email, isAdmin })
    return { ok: true, email, isAdmin }
  } catch (err) {
    return { ok: false, reason: friendlyAuthError(err) }
  }
}

// signup: create a brand-new credentialed account (fresh slate — abandons the
// current guest progress). Most players use claimAccount instead; this exists
// for completeness / a deliberate "start over with an account" path.
export async function signup(emailInput, password) {
  if (!online) return { ok: false, reason: 'offline' }
  try {
    const { data, error } = await supabase.auth.signUp({ email: emailInput, password })
    if (error) throw error
    // When "Confirm email" is ON in Supabase, signUp returns a user but NO
    // session until they click the verification link. Don't try to play yet —
    // tell the caller to show a "check your email" screen.
    if (!data.session) {
      return { ok: true, needsConfirmation: true, email: data.user?.email ?? emailInput }
    }
    userId = data.user?.id ?? userId
    email = data.user?.email ?? emailInput
    if (bindOwner(userId) === 'reload') return { ok: true, email, isAdmin }
    // The account exists at this point. If the profile fetch hiccups, carry on —
    // the sync ticker retries — rather than reporting the whole sign-up as failed.
    try { await refreshAccountMeta() } catch (e) { console.warn('[account] profile fetch after signup failed:', e) }
    emit(Events.AccountReady, { userId, online: true, loggedIn: true, email, isAdmin, membership })
    return { ok: true, email, isAdmin }
  } catch (err) {
    return { ok: false, reason: friendlyAuthError(err) }
  }
}

// login: authenticate to an existing account on this (or a new) device, then
// adopt the server's saved state.
export async function login(emailInput, password) {
  if (!online) return { ok: false, reason: 'offline' }
  try {
    const { data, error } = await supabase.auth.signInWithPassword({ email: emailInput, password })
    if (error) throw error
    userId = data.user?.id ?? null
    email = data.user?.email ?? emailInput
    if (bindOwner(userId) === 'reload') return { ok: true, email, isAdmin } // another player's game was here: reloading clean
    await ensureTabLock()
    await refreshAccountMeta()
    await syncOnSession() // adopt this account's saved game
    emit(Events.AccountReady, { userId, online: true, loggedIn: true, email, isAdmin, membership })
    emit(Events.AccountStatePulled)
    return { ok: true, email, isAdmin }
  } catch (err) {
    return { ok: false, reason: friendlyAuthError(err) }
  }
}

// loginWithGoogle: OAuth redirect flow for BOTH login and register. On return,
// detectSessionInUrl + init() pick up the session. A brand-new Google account
// lands logged-in with no cat yet → the app routes it into the adopt-a-Mao flow
// to pick its kingdom; a returning Google account goes straight to its kingdom.
export async function loginWithGoogle() {
  if (!online) return { ok: false, reason: 'offline' }
  try {
    const redirectTo = typeof window !== 'undefined' ? window.location.origin : undefined
    const { error } = await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo } })
    if (error) throw error
    return { ok: true } // browser redirects to Google; nothing more to do here
  } catch (err) {
    return { ok: false, reason: friendlyAuthError(err) }
  }
}

// Send a password-reset email (frictionless email mode makes this usable). The
// reset link returns to the app where the SDK opens a recovery session.
export async function requestPasswordReset(emailInput) {
  try {
    const redirectTo = typeof window !== 'undefined' ? window.location.origin : undefined
    const { error } = await supabase.auth.resetPasswordForEmail(emailInput, { redirectTo })
    if (error) throw error
    return { ok: true }
  } catch (err) {
    return { ok: false, reason: friendlyAuthError(err) }
  }
}

// logout: sign out → logged-OUT state (no anonymous guest). The header then
// shows Log in / Register; the player's account is untouched on the server.
export async function logout() {
  leaving = true
  // 1) Save anything unsaved while we still have a session (the last few seconds of play must not be lost).
  if (userId && online && dirty && !staleLock) {
    try { await Promise.race([pushState(), new Promise((_, rej) => setTimeout(() => rej(new Error('save timed out')), 5000))]) }
    catch (err) { console.warn('[account] final save before sign-out failed:', err.message) }
  }
  email = null
  isAdmin = false
  membership = 'FREE'
  userId = null
  try {
    await supabase.auth.signOut()
    online = true
  } catch {
    online = false
  }
  // 2) Forget this player's game on this device and start clean, so the next person to sign in here (or a brand-new
  //    account) can never inherit it — or overwrite their own saved game with it. Freeze saving FIRST: the game keeps
  //    running until the page reloads and would otherwise write the old game straight back.
  freezePersistence()
  wipeLocalGame()
  emit(Events.AccountReady, { userId: null, online, loggedIn: false, email: null, isAdmin: false })
  if (typeof window !== 'undefined') window.location.assign(window.location.pathname)
  return { ok: true }
}

// ── Billing (Kingdom Club via Stripe) ──────────────────────────────────────
// Ask the server for a checkout URL (real Stripe session or payment link with
// our userId embedded) and open it. Returns { ok, url } or { ok:false }.
export async function startCheckout() {
  if (!online) return { ok: false, reason: 'offline' }
  try {
    const res = await api('/api/billing/checkout', { method: 'POST', body: '{}' })
    if (res?.url && typeof window !== 'undefined') window.open(res.url, '_blank')
    return { ok: true, url: res.url }
  } catch (err) {
    // 501 = billing not configured → caller can offer the dev path.
    return { ok: false, reason: String(err.message || err), notConfigured: /501/.test(String(err.message || err)) }
  }
}

// CR-7 coin packs — the only real-money product. Asks the server for a one-time
// Stripe Checkout URL for `packId` and opens it. Returns { notConfigured: true }
// when Stripe isn't wired (local/tester), so the Shop can fall back to the
// dev-grant path (credit locally + push).
export async function buyCoins(packId) {
  if (!online) return { ok: false, reason: 'offline' }
  try {
    const res = await api('/api/billing/coins', { method: 'POST', body: JSON.stringify({ packId }) })
    // Caller navigates to res.url (same-tab) so the success redirect returns to
    // a single app instance that credits + pushes — no cross-tab race.
    return { ok: true, url: res.url }
  } catch (err) {
    const msg = String(err.message || err)
    return { ok: false, reason: msg, notConfigured: /501/.test(msg) }
  }
}

// Coin purchases go through a server-side ledger (see server/index.js):
//   confirmCoins(sessionId)  – on return from Checkout: the server asks Stripe whether it's really paid, and records it
//   fetchCoinsTotal()        – the running total of coins this player has paid for (includes purchases the webhook recorded
//                              while the player was away). The game adds only the part it hasn't added yet.
export async function confirmCoins(sessionId) {
  if (!online) return { ok: false, reason: 'offline' }
  try {
    const res = await api('/api/billing/coins/confirm', { method: 'POST', body: JSON.stringify({ sessionId }) })
    return { ok: !!res.ok, total: res.total ?? 0 }
  } catch (err) {
    return { ok: false, reason: String(err.message || err), notPaid: err.status === 409 }
  }
}
export async function fetchCoinsTotal() {
  if (!online || !userId) return { ok: false, total: 0 }
  try {
    const res = await api('/api/billing/coins/total')
    return { ok: !!res.ok, total: Number(res.total) || 0 }
  } catch { return { ok: false, total: 0 } }
}
// What payments are set up as: { configured, mode: 'test' | 'live' | null }. Used for the Shop's TEST MODE badge.
export async function getBillingStatus() {
  try { return await api('/api/billing/status') } catch { return { configured: false, mode: null } }
}

// Refresh membership from the server (call after returning from Stripe, or on
// a "I paid but it didn't update" retry). The webhook is the real trigger;
// this just re-reads the authoritative account record.
export async function refreshMembership() {
  if (!online) return membership
  try {
    await refreshAccountMeta()
    return membership
  } catch {
    return membership
  }
}

// Dev / admin shortcut — flip membership without real billing (server gates
// this when Stripe is configured for non-admins).
export async function devActivateMembership(tier = 'KINGDOM_CLUB') {
  if (!online) return { ok: false, reason: 'offline' }
  try {
    const res = await api('/api/billing/dev-activate', { method: 'POST', body: JSON.stringify({ tier }) })
    membership = res.membership ?? tier
    emit(Events.MembershipChange, { tier: membership })
    return { ok: true, membership }
  } catch (err) {
    return { ok: false, reason: String(err.message || err) }
  }
}

function friendlyAuthError(err) {
  const m = String(err?.message ?? '')
  // supabase-js errors carry .status; our own api() errors put the code in the message.
  const status = Number(err?.status) || Number((m.match(/\b([45]\d\d)\b/) || [])[1]) || 0
  if (status === 409 || /already (been )?registered|already exists/i.test(m)) return 'That email is already registered.'
  if (status === 401 || /invalid login credentials/i.test(m)) return 'Wrong email or password.'
  if (status === 429 || /rate limit|too many/i.test(m)) return 'Too many attempts — wait a minute and try again.'
  if (/password/i.test(m) && (status === 400 || status === 422)) return 'That password is too weak (min 6 characters).'
  if (status === 400 || status === 422) return 'Check your email + password (min 6 chars).'
  if (err instanceof TypeError || /failed to fetch|networkerror|load failed/i.test(m)) return 'Network error — try again.'
  // Anything else: show the real reason instead of pretending it was the network.
  return m ? `Something went wrong: ${m}` : 'Network error — try again.'
}

export async function fetchKingdomAggregates() {
  return api('/api/kingdoms/stats', { headers: { authorization: '' } })
}

// Fetch a player's public registry sheet. Always returns the basics (name,
// kingdom, wealth, achievements map, etc.). When the logged-in viewer is a
// Kingdom Club member (or admin), the response also includes counters +
// real-estate breakdown + license/degree lists — the full deep-view.
export async function fetchPublicRegistry(userId) {
  if (!online) throw new Error('offline')
  return api(`/api/players/${encodeURIComponent(userId)}/registry`)
}

// ── REGISTRY + CONNECT API ──────────────────────────────────────────────────
// list() is public (a signed-in viewer's token, if any, adds each row's connection state); the rest need a login.
export const registryApi = {
  list: (params = {}) => api(`/api/registry?${new URLSearchParams(Object.entries(params).filter(([, v]) => v !== '' && v != null))}`),
  connections: () => api('/api/connections'),
  request: (toId) => api('/api/connections/request', { method: 'POST', body: JSON.stringify({ toId }) }),
  accept: (requestId) => api('/api/connections/accept', { method: 'POST', body: JSON.stringify({ requestId }) }),
  decline: (requestId) => api('/api/connections/decline', { method: 'POST', body: JSON.stringify({ requestId }) }),
  remove: (userId) => api('/api/connections/remove', { method: 'POST', body: JSON.stringify({ userId }) }),
}

// Choose the Mao's gender (once): the server keeps the FIRST value it hears and returns what is locked.
export async function lockMyGender(g) {
  const r = await api('/api/profile/gender', { method: 'POST', body: JSON.stringify({ gender: g }) })
  gender = r.gender ?? gender
  return r
}

// ── CO-OP INVITATIONS API (docs/COOP-INVITES-PLAN.md) ────────────────────────
export const inviteApi = {
  list: () => api('/api/invites'),
  lookup: (no) => api(`/api/registry/lookup?no=${encodeURIComponent(no)}`),
  create: (body) => api('/api/invites', { method: 'POST', body: JSON.stringify(body) }),
  accept: (id) => api('/api/invites/accept', { method: 'POST', body: JSON.stringify({ id }) }),
  decline: (id) => api('/api/invites/decline', { method: 'POST', body: JSON.stringify({ id }) }),
  cancel: (id) => api('/api/invites/cancel', { method: 'POST', body: JSON.stringify({ id }) }),
  block: (hostId) => api('/api/invites/block', { method: 'POST', body: JSON.stringify({ hostId }) }),
  unblock: (hostId) => api('/api/invites/unblock', { method: 'POST', body: JSON.stringify({ hostId }) }),
  seen: (ids) => api('/api/invites/seen', { method: 'POST', body: JSON.stringify({ ids }) }),
}

// ── MARRIAGE PROPOSALS + THE HOUSEHOLD (docs/MARRIAGE-PLAN.md) ─────────────────────────────────────────────
export const proposalApi = {
  create: (body) => api('/api/proposals', { method: 'POST', body: JSON.stringify(body) }),
  accept: (id) => api('/api/proposals/accept', { method: 'POST', body: JSON.stringify({ id }) }),
  decline: (id) => api('/api/proposals/decline', { method: 'POST', body: JSON.stringify({ id }) }),
  cancel: (id) => api('/api/proposals/cancel', { method: 'POST', body: JSON.stringify({ id }) }),
  block: (proposerId) => api('/api/proposals/block', { method: 'POST', body: JSON.stringify({ proposerId }) }),
  unblock: (proposerId) => api('/api/proposals/unblock', { method: 'POST', body: JSON.stringify({ proposerId }) }),
  seen: (ids) => api('/api/proposals/seen', { method: 'POST', body: JSON.stringify({ ids }) }),
  divorce: (marriageId) => api('/api/marriages/divorce', { method: 'POST', body: JSON.stringify({ marriageId }) }),
  household: () => api('/api/household'),
  householdSeen: () => api('/api/household/seen', { method: 'POST' }),
}
// the visit itself: the guest works, the host pays in, either side ends it
const stayPost = (what, body) => api(`/api/stays/${what}`, { method: 'POST', body: JSON.stringify(body) })
export const stayApi = {
  work: (stayId) => stayPost('work', { stayId }),
  deposit: (stayId, amount) => stayPost('deposit', { stayId, amount }),
  count: (stayId) => stayPost('count', { stayId }),
  oust: (stayId) => stayPost('oust', { stayId }),
  leave: (stayId) => stayPost('leave', { stayId }),
  heist: (stayId) => stayPost('heist', { stayId }),
  seize: (stayId) => stayPost('seize', { stayId }),
  kingdom: (stayId) => api(`/api/stays/${encodeURIComponent(stayId)}/kingdom`), // the host's real kingdom, read-only (a guest on an active stay)
  guestbook: () => api('/api/guestbook'),
}

// Mirror server-awarded "firsts" trophies (e.g. inaugural Kingdom Club
// member) into local achievements. Idempotent — awardServerFirst() no-ops
// if the badge is already unlocked, so re-running it on every account-me
// fetch is safe and avoids duplicate narrative-beat celebrations.
function applyServerFirsts(firsts) {
  if (!firsts || typeof firsts !== 'object') return
  for (const token of Object.keys(firsts)) {
    awardServerFirst(token)
  }
}

// ── SUPERADMIN API (only succeeds when the logged-in account isAdmin) ───────
export const admin = {
  overview: () => api('/api/admin/overview'),
  users: (q = '') => api(`/api/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  user: (id) => api(`/api/admin/users/${id}`),
  grant: (id, amount) =>
    api(`/api/admin/users/${id}/grant`, { method: 'POST', body: JSON.stringify({ amount }) }),
  ban: (id, banned) =>
    api(`/api/admin/users/${id}/ban`, { method: 'POST', body: JSON.stringify({ banned }) }),
  setAdmin: (id, makeAdmin) =>
    api(`/api/admin/users/${id}/admin`, { method: 'POST', body: JSON.stringify({ isAdmin: makeAdmin }) }),
}
